import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaDraftingCompass, FaCogs, FaChartLine, FaHandshake, FaLightbulb, FaSyncAlt, FaCheckCircle } from 'react-icons/fa';
import './OurApproach.css';

const OurApproach = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="approach-page">
            {/* Hero Section */}
            <section className="approach-hero section" data-aos="fade-up" data-aos-mirror="true">
                <div className="container">
                    <div className="approach-hero-content">
                        <h2 className="hero-title">
                            Diagnose, Design, <span className="highlight-teal">Deliver</span>
                        </h2>
                        <div className="hero-subtitle-wrapper">
                            <h3 className="hero-tagline">A Structured Path from Clarity to Execution</h3>
                            <p className="hero-description">
                                At Triology, we believe that meaningful transformation does not begin with solutions it begins with understanding. Our consulting approach follows a structured, repeatable journey that helps organizations move from ambiguity to aligned execution.
                            </p>

                            {/* Visual Highlights */}
                            <div className="ddd-grid">
                                <div className="ddd-item">
                                    <span className="ddd-number">1</span>
                                    <span className="ddd-label">Diagnose</span>
                                </div>
                                <div className="ddd-item">
                                    <span className="ddd-number">2</span>
                                    <span className="ddd-label">Design</span>
                                </div>
                                <div className="ddd-item">
                                    <span className="ddd-number">3</span>
                                    <span className="ddd-label">Deliver</span>
                                </div>
                            </div>

                            <p className="hero-footer-text">
                                Every engagement at Triology is anchored in three integrated stages. This approach ensures that decisions are grounded in reality, solutions are context-specific, and execution is disciplined and sustainable.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Diagnosis Comes First Section */}
            <section className="diagnosis-section section" data-aos="fade-up" data-aos-mirror="true">
                <div className="container">
                    <div className="diagnosis-header-center" style={{ textAlign: 'center', margin: '0 auto 3rem auto', maxWidth: '800px' }}>
                        <span className="diagnosis-label">WHY DIAGNOSIS COMES FIRST</span>
                        <h2 className="diagnosis-title">Most problems are symptoms, not root causes</h2>
                    </div>

                    <div className="diagnosis-container">
                        <div className="diagnosis-text">
                            <p className="diagnosis-desc">
                                Organizations often respond to challenges by introducing new initiatives, tools, or restructures. Without understanding the underlying system, these actions create temporary relief not lasting change.
                            </p>

                            <div className="diagnosis-cards-row">
                                <div className="diagnosis-point-card">
                                    <FaCheckCircle className="card-icon" />
                                    <p>Triology begins every engagement with diagnosis to surface root causes beneath visible issues</p>
                                </div>
                                <div className="diagnosis-point-card">
                                    <FaCheckCircle className="card-icon" />
                                    <p>Understand how leadership, systems, and people interact</p>
                                </div>
                                <div className="diagnosis-point-card">
                                    <FaCheckCircle className="card-icon" />
                                    <p>Identify execution risks before solutions are designed</p>
                                </div>
                            </div>

                            <p className="diagnosis-footer">
                                "Diagnosis creates clarity, and clarity reduces execution failure."
                            </p>
                        </div>


                    </div>
                </div>
            </section>

            {/* DIAGNOSE Section */}
            <section className="leap-stage-section" data-aos="fade-up">
                <div className="leap-stage-content">
                    <div className="leap-stage-header">
                        <span className="leap-stage-letter">D</span>
                        <h2 className="leap-stage-title">DIAGNOSE</h2>
                        <p>Understanding the Organization as a System</p>
                    </div>
                    <div className="leap-stage-description">
                        <p>
                            Triology’s diagnostic phase evaluates organizational health across leadership, strategy, execution, people, and future readiness.
                        </p>

                        <div className="leap-list-container">
                            <h4>We assess:</h4>
                            <ul className="leap-list">
                                <li>Clarity of direction and priorities</li>
                                <li>Leadership alignment and decision-making</li>
                                <li>Operating structures and governance</li>
                                <li>Execution rhythm and accountability</li>
                                <li>Capability and capacity to scale</li>
                            </ul>
                        </div>

                        <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                            This is not an audit or a checklist exercise. It is a structured, insight-driven process that results in:
                            clear problem statements, prioritized focus areas, and shared understanding across leadership.
                        </p>

                        <div className="leap-outcome">
                            <h4>Outcome of Diagnose</h4>
                            <p>A fact-based, organization-wide clarity on what truly needs attention and why.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* DESIGN Section */}
            <section className="leap-stage-section" data-aos="fade-up">
                <div className="leap-stage-content">
                    <div className="leap-stage-header">
                        <span className="leap-stage-letter">D</span>
                        <h2 className="leap-stage-title">DESIGN</h2>
                        <p>Converting Intent into Execution-Ready Systems</p>
                    </div>
                    <div className="leap-stage-description">
                        <p>
                            Once clarity is established, Triology moves into design. This stage focuses on translating leadership intent into practical systems that people can operate every day.
                        </p>

                        <div className="leap-list-container">
                            <h4>Design includes:</h4>
                            <ul className="leap-list">
                                <li>Strategy and priority architecture</li>
                                <li>Operating and governance structures</li>
                                <li>Role clarity and decision rights</li>
                                <li>Process and performance systems</li>
                                <li>Capability and leadership roadmaps</li>
                            </ul>
                        </div>

                        <p style={{ marginBottom: '1.5rem' }}>
                            Design at Triology is not theoretical. Every design decision is tested against execution reality, organizational maturity, and leadership bandwidth.
                        </p>

                        <div className="leap-outcome">
                            <h4>Outcome of Design</h4>
                            <p>Clear systems, structures, and roadmaps that align strategy, people, and execution.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* DELIVER Section */}
            <section className="leap-stage-section" data-aos="fade-up" style={{ borderBottom: 'none' }}>
                <div className="leap-stage-content">
                    <div className="leap-stage-header">
                        <span className="leap-stage-letter">D</span>
                        <h2 className="leap-stage-title">DELIVER</h2>
                        <p>Embedding Discipline into Execution</p>
                    </div>
                    <div className="leap-stage-description">
                        <p>
                            Execution is where most transformations fail not because plans are weak, but because governance and ownership are unclear.
                            Triology addresses this through Deliver, supported by COSMOS our Consulting & Project Management Operating System.
                        </p>

                        <div className="leap-list-container">
                            <h4>Deliver focuses on:</h4>
                            <ul className="leap-list">
                                <li>Clear ownership and accountability</li>
                                <li>Milestone-based execution planning</li>
                                <li>Structured review and decision cadence</li>
                                <li>Tracking progress and removing bottlenecks</li>
                                <li>Enabling leaders and teams to execute confidently</li>
                            </ul>
                        </div>

                        <p style={{ marginBottom: '1.5rem' }}>
                            We do not “hand over” plans and exit. We stay engaged to ensure systems are lived, decisions are taken, and momentum is sustained.
                        </p>

                        <div className="leap-outcome">
                            <h4>Outcome of Deliver</h4>
                            <p>Disciplined execution through clear ownership, governance, and continuous tracking.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final Quote Section */}
            <section className="section" style={{ padding: '4rem 0', background: '#ffffff', textAlign: 'center' }}>
                <div className="container">
                    <blockquote style={{ maxWidth: '900px', margin: '0 auto', fontSize: '1.4rem', fontStyle: 'italic', fontWeight: '600', color: 'var(--primary-color)', lineHeight: '1.6' }}>
                        "Triology’s role is catalytic we enable clarity, design systems, and embed execution discipline, while ensuring transformation is owned and sustained by the organization."
                    </blockquote>
                </div>
            </section>


            {/* Risk & Engagement Section */}
            <section className="risk-engagement-section" data-aos="fade-up">
                <div className="risk-engagement-grid">
                    {/* Left Side: Risk Reduction */}
                    <div className="risk-card">
                        <span className="re-title">HOW OUR APPROACH REDUCES RISK</span>
                        <h3 className="re-subtitle">Why Diagnose–Design–Deliver Works</h3>
                        <p className="re-text">This structured approach:</p>
                        <ul className="re-list">
                            <li>Prevents premature solutions</li>
                            <li>Reduces execution surprises</li>
                            <li>Aligns leadership before large investments</li>
                            <li>Builds internal capability, not dependency</li>
                            <li>Ensures change sustains beyond the engagement</li>
                        </ul>
                        <p className="re-text" style={{ fontStyle: 'italic', marginTop: '1rem' }}>
                            It is especially effective for organizations navigating growth, transition, or complexity.
                        </p>
                    </div>

                    {/* Right Side: Client Engagement */}
                    <div className="engagement-card">
                        <span className="re-title">HOW WE ENGAGE WITH CLIENTS</span>
                        <h3 className="re-subtitle">A Partnership Model, Not a Vendor Model</h3>
                        <p className="re-text">
                            Triology works alongside leadership teams as thought partners and execution enablers. Our role is to bring clarity, structure, and discipline while ownership remains firmly within the organization.
                        </p>
                        <p className="re-text">We adapt the depth and pace of engagement based on:</p>
                        <ul className="re-list">
                            <li>Organizational maturity</li>
                            <li>Leadership readiness</li>
                            <li>Complexity of the challenge</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Insight to Impact Section */}
            <section className="insight-section">
                <div className="container">
                    <div className="insight-card">
                        <h2 className="insight-title">From Insight to Impact</h2>
                        <p className="insight-text">
                            Triology’s approach ensures that clarity leads to alignment, alignment leads to execution, and execution leads to sustainable impact.
                        </p>
                        <p className="insight-text" style={{ fontWeight: '500', marginBottom: 0 }}>
                            Before accelerating growth, we help organizations ensure that their systems, leadership, and execution are truly ready.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OurApproach;
